import { useEffect, useState } from 'react'
import { ChevronDown, ChevronUp, FileText, Mail, User } from 'lucide-react'
import { EmailBody } from '@/components/EmailBody'
import type { AttachmentReference, OrderConfirmation } from '@/types/orderConfirmation'
import { DocumentViewer } from '@/features/documents/DocumentViewer'

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '-'
  try {
    const d = new Date(iso)
    const y = d.getFullYear()
    const mo = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const h = String(d.getHours()).padStart(2, '0')
    const min = String(d.getMinutes()).padStart(2, '0')
    return `${y}-${mo}-${day} ${h}:${min}`
  } catch { return iso }
}

function formatEmailHeaderDate(iso: string | null | undefined): string {
  if (!iso) return ''
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(iso))
  } catch {
    return formatDateTime(iso)
  }
}

function bareEmailFromAddress(address: string): string {
  const match = address.trim().match(/<([^>]+)>/)
  return match?.[1]?.trim() ?? address.trim()
}

function senderDisplayFromAddress(address: string): { name: string; initial: string; email: string } {
  const trimmed = address.trim()
  const match = trimmed.match(/^([^<]+)<([^>]+)>$/)
  const display = match ? match[1].trim().replace(/^["']|["']$/g, '') : ''
  const emailOnly = bareEmailFromAddress(trimmed)
  const localPart = emailOnly.includes('@') ? emailOnly.split('@')[0] : emailOnly
  const name = display && display !== emailOnly
    ? display
    : localPart.replace(/[._-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  const initial = (name[0] ?? emailOnly[0] ?? '?').toUpperCase()
  return { name, initial, email: emailOnly }
}

export function DocumentPreview() {
  return (
    <div className="relative w-full border border-sheet-border bg-white shadow-sm"
      style={{ clipPath: 'polygon(0 0, calc(100% - 24px) 0, 100% 24px, 100% 100%, 0 100%)' }}>
      <div className="absolute right-0 top-0 h-6 w-6 border-b border-l border-sheet-border bg-gray-50" />
      <div className="space-y-4 px-5 py-6">
        {[65, 50, 80, 55, 70].map((w, i) => (
          <div key={i} className="space-y-1.5">
            <div className="h-1.5 rounded-full bg-gray-200" style={{ width: `${w}%` }} />
            <div className="h-1.5 rounded-full bg-gray-100" style={{ width: `${w * 0.6}%` }} />
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * The parts of a source email this section renders. Order confirmations and customer orders
 * carry the same shape under different names, so it is named structurally rather than taking
 * whichever document it hangs off.
 */
export interface SourceEmail {
  subject: string
  sender_address: string
  received: string
  body: string
}

export function EmailSourceSection({
  label,
  email,
  attachment,
  resetKey,
  prominent = false,
  defaultExpanded = true,
}: {
  /** Omitted where the surrounding panel already says what this is. */
  label?: string
  email: SourceEmail
  /** Listed under the body when the document arrived with one. */
  attachment?: AttachmentReference | null
  /** Collapses back to the default when this changes — the id of the document being shown. */
  resetKey?: string
  prominent?: boolean
  defaultExpanded?: boolean
}) {
  const [expanded, setExpanded] = useState(defaultExpanded)

  useEffect(() => {
    setExpanded(defaultExpanded)
  }, [resetKey, defaultExpanded])

  if (prominent) {
    const sender = senderDisplayFromAddress(email.sender_address)
    const receivedLabel = formatEmailHeaderDate(email.received)

    return (
      <div className="overflow-hidden rounded-sheet border border-sheet-border bg-white shadow-sm">
        <div className="border-b border-gray-100 px-5 pb-4 pt-5">
          <h1 className="text-[1.375rem] font-normal leading-snug tracking-tight text-gray-900">
            {email.subject || '(no subject)'}
          </h1>
          <div className="mt-4 flex items-start gap-3">
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-600 text-sm font-medium text-white"
              aria-hidden
            >
              {sender.initial}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">{sender.name}</p>
                  <p className="text-xs text-gray-500">{sender.email}</p>
                  <p className="mt-0.5 text-xs text-gray-500">to me</p>
                </div>
                {receivedLabel && (
                  <p className="shrink-0 text-xs text-gray-500">{receivedLabel}</p>
                )}
              </div>
            </div>
          </div>
        </div>
        {email.body ? (
          <div className="max-h-[min(60vh,560px)] overflow-y-auto px-5 py-4">
            <EmailBody html={email.body} plainTextClassName="text-[0.8125rem] leading-[1.5] text-gray-800" />
          </div>
        ) : (
          <p className="px-5 py-4 text-sm text-gray-400">(no body)</p>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {label && (
        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      )}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-start gap-3 rounded-md px-0 py-1 text-left hover:bg-gray-50"
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-50">
          <Mail className="h-5 w-5 text-blue-400" />
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          <p className="text-sm font-medium leading-snug text-gray-800">Created from email</p>
          <p className="mt-0.5 text-xs text-gray-400">at {formatDateTime(email.received)}</p>
          <p className="mt-1 truncate text-xs text-gray-500">{email.sender_address}</p>
        </div>
        <div className="mt-1 shrink-0">
          {expanded
            ? <ChevronUp className="h-4 w-4 text-gray-400" />
            : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
      </button>

      {expanded && (
        <div className="mt-1 overflow-hidden rounded-sheet border border-sheet-border bg-white shadow-sm">
          <div className="border-b border-gray-100 px-4 py-3">
            <p className="text-sm font-semibold text-gray-900">{email.subject || '(no subject)'}</p>
            <p className="mt-0.5 text-xs text-gray-500">
              <span className="font-medium">From:</span> {email.sender_address}
            </p>
            <p className="text-xs text-gray-400">{formatDateTime(email.received)}</p>
          </div>
          {email.body ? (
            <div className="max-h-[480px] overflow-y-auto px-4 py-3">
              <EmailBody html={email.body} plainTextClassName="text-xs leading-relaxed text-gray-700" />
            </div>
          ) : (
            <p className="px-4 py-3 text-xs text-gray-400">(no body)</p>
          )}
          {attachment && (
            <div className="flex items-center gap-2 border-t border-gray-100 bg-gray-50 px-4 py-2.5">
              <FileText className="h-4 w-4 shrink-0 text-gray-400" />
              <span className="truncate text-xs text-gray-600">{attachment.name}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function ManualSourceSection({ label, oc }: { label: string; oc: OrderConfirmation }) {
  const creator = oc.source.created_by
  const headline = creator
    ? `Manually created by ${creator.first_name} ${creator.last_name}`
    : 'Manually created'

  return (
    <div className="space-y-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100">
          <User className="h-5 w-5 text-gray-500" />
        </div>
        <div className="min-w-0 pt-0.5">
          <p className="text-sm font-medium leading-snug text-gray-800">{headline}</p>
          <p className="mt-0.5 text-xs text-gray-400">at {formatDateTime(oc.received)}</p>
          {creator && (
            <p className="mt-1 truncate text-xs text-gray-500">{creator.email_address}</p>
          )}
        </div>
      </div>
    </div>
  )
}

export function AttachmentSection({
  attachment,
  prominent = false,
  label = 'Attachment',
}: {
  attachment: AttachmentReference
  prominent?: boolean
  /** What the section is called where "attachment" is not the useful word for it. */
  label?: string
}) {
  return (
    <div className="space-y-3">
      <p className={prominent ? 'text-xs font-semibold uppercase tracking-wide text-gray-500' : 'text-[10px] font-semibold uppercase tracking-wide text-gray-400'}>
        {label}
      </p>
      <div className={prominent ? 'min-h-[min(50vh,480px)]' : undefined}>
        <DocumentViewer attachmentId={attachment.id} name={attachment.name} />
      </div>
    </div>
  )
}
